/**
 * Settings Context
 *
 * React context for managing plugin settings state across the application.
 *
 * @since 1.0.0
 */

import apiFetch from '@wordpress/api-fetch';
import { createContext, useCallback, useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import type { PageOption, SettingsData } from '../../components/settings/types';
import { useNavigationGuard } from '../../hooks';
import { useNotification } from '../notification-context';
import type { SettingsContextValue, SettingsProviderProps } from './types';

/**
 * Settings context
 *
 * @since 1.0.0
 */
export const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

/**
 * Settings context provider component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const SettingsProvider = ({ children }: SettingsProviderProps): JSX.Element => {
	const [loading, setLoading] = useState<boolean>(true);
	const [saving, setSaving] = useState<boolean>(false);
	const [settings, setSettings] = useState<SettingsData | null>(null);
	const [originalSettings, setOriginalSettings] = useState<SettingsData | null>(null);
	const [wordpressPages, setWordpressPages] = useState<PageOption[]>([]);
	const [thankYouPages, setThankYouPages] = useState<PageOption[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const { setHasUnsavedChanges } = useNavigationGuard();
	const { addNotification } = useNotification();

	// Fetch settings and page options on mount
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);

				// Fetch current settings
				const settingsResponse = await apiFetch<{ success: boolean; data: SettingsData }>({
					path: '/redfox-thank-you/v1/settings',
				});

				// Fetch WordPress pages from custom API
				const wpPagesResponse = await apiFetch<{ success: boolean; data: Array<{ id: number; title: string }> }>({
					path: '/redfox-thank-you/v1/settings/wordpress-pages',
				});

				// Fetch Thank You pages from custom API
				const tyPagesResponse = await apiFetch<{ success: boolean; data: Array<{ id: number; title: string }> }>({
					path: '/redfox-thank-you/v1/settings/thank-you-pages',
				});

				setSettings(settingsResponse.data);
				setOriginalSettings(settingsResponse.data);
				setWordpressPages([
					{ value: '', label: __('Auto-created Thank You Page', 'redfox-thank-you') },
					...wpPagesResponse.data.map((page) => ({
						value: String(page.id),
						label: page.title || __('(No title)', 'redfox-thank-you'),
					})),
				]);
				setThankYouPages([
					{ value: '', label: __('None (Use Default)', 'redfox-thank-you') },
					...tyPagesResponse.data.map((page) => ({
						value: String(page.id),
						label: page.title || __('(No title)', 'redfox-thank-you'),
					})),
				]);

				setLoading(false);
			} catch (err) {
				const errorMessage = err instanceof Error
					? err.message
					: __('Failed to load settings', 'redfox-thank-you');
				setError(errorMessage);
				addNotification('error', errorMessage);
				setLoading(false);
			}
		};

		fetchData();
	}, [addNotification]);

	// Update a single setting
	const updateSetting = useCallback(<K extends keyof SettingsData>(
		key: K,
		value: SettingsData[K],
	) => {
		if (!settings) return;
		setSettings({
			...settings,
			[key]: value,
		});
		setSuccessMessage(null);
		setError(null);
		setHasUnsavedChanges(true);
	}, [settings, setHasUnsavedChanges]);

	// Save settings to the server
	const saveSettings = useCallback(async () => {
		if (!settings) return;

		try {
			setSaving(true);
			setError(null);

			await apiFetch({
				path: '/redfox-thank-you/v1/settings',
				method: 'POST',
				data: settings,
			});

			setOriginalSettings(settings);
			const successMsg = __('Settings saved successfully!', 'redfox-thank-you');
			setSuccessMessage(successMsg);
			addNotification('success', successMsg);
			setHasUnsavedChanges(false);
			setSaving(false);
		} catch (err) {
			const errorMessage = err instanceof Error
				? err.message
				: __('Failed to save settings', 'redfox-thank-you');
			setError(errorMessage);
			addNotification('error', errorMessage);
			setSaving(false);
		}
	}, [settings, setHasUnsavedChanges, addNotification]);

	// Reset settings to defaults
	const resetSettings = useCallback(async () => {
		try {
			setSaving(true);
			setError(null);

			const resetResponse = await apiFetch<{ success: boolean; data: SettingsData }>({
				path: '/redfox-thank-you/v1/settings/reset',
				method: 'POST',
			});

			setSettings(resetResponse.data);
			setOriginalSettings(resetResponse.data);
			const successMsg = __('Settings reset to defaults successfully!', 'redfox-thank-you');
			setSuccessMessage(successMsg);
			addNotification('success', successMsg);
			setHasUnsavedChanges(false);
			setSaving(false);
		} catch (err) {
			const errorMessage = err instanceof Error
				? err.message
				: __('Failed to reset settings', 'redfox-thank-you');
			setError(errorMessage);
			addNotification('error', errorMessage);
			setSaving(false);
		}
	}, [setHasUnsavedChanges, addNotification]);

	// Reset changes to original settings
	const resetChanges = useCallback(() => {
		if (!originalSettings) return;

		setSettings(originalSettings);
		setSuccessMessage(null);
		setHasUnsavedChanges(false);
	}, [originalSettings, setHasUnsavedChanges]);

	// Clear error message
	const clearError = useCallback(() => {
		setError(null);
	}, []);

	// Clear success message
	const clearSuccessMessage = useCallback(() => {
		setSuccessMessage(null);
	}, []);

	const value: SettingsContextValue = {
		loading,
		saving,
		settings,
		originalSettings,
		wordpressPages,
		thankYouPages,
		error,
		successMessage,
		updateSetting,
		saveSettings,
		resetSettings,
		resetChanges,
		clearError,
		clearSuccessMessage,
	};

	return (
		<SettingsContext.Provider value={value}>
			{children}
		</SettingsContext.Provider>
	);
};


